import React from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
  DATE_TIME_FORMAT,
  USERS_WITH_RECHARGE_SUB_ID,
} from '../../../constants';
import { SubNav } from '../../SubNav/simo';
import UserInfo from '../../../containers/UserInfo';
import { prettyKey, toGb } from 'helpers/formatters';
import EditableRow from 'components/EditableRow';
import { can, permActions } from '../../../permissions';
import { simoRenderPlan } from 'helpers/subs';

export default ({
  sub,
  diagnosticDataLoadingStatus,
  diagnosticData,
  userId,
  handleUpdateField,
  updateSimoSubCycleType,
  subId,
  currentCycle,
  simoUsageBalance,
  plans,
  loggedInUser,
}) => {
  const plan = plans.find((plan) => plan.id === sub?.plan?.id);
  const upcomingPlan = plans.find((plan) => plan.id === sub?.upcoming_plan?.id);
  const upcomingPlanToDisplay =
    upcomingPlan || (sub.simo_status === 'active' && plan) || null;
  const showSubPurchases = can(loggedInUser, permActions.VIEW_PURCHASES);
  const showPrices = can(loggedInUser, permActions.VIEW_PRICES);
  const balance = simoUsageBalance?.balance;

  return (
    <div className="Sub">
      <div className="flex-justify-space-between d-flex-vc">
        <h1 style={{ marginTop: 0 }}>Simo Subscription details</h1>
        {showSubPurchases && (
          <div>
            <div>
              <Link
                to={`/users/${userId}/purchases?subscription=${subId}&subCarrier=simo`}
              >
                Subscription purchases
              </Link>
            </div>
          </div>
        )}
      </div>
      <SubNav sub={sub} isBackToSub={false} withDeviceLink />
      <table className="table">
        <tbody>
          <tr>
            <td>ID</td>
            <td>{sub.id}</td>
            <td></td>
          </tr>
          {USERS_WITH_RECHARGE_SUB_ID.includes(sub.user_profile) && (
            <tr>
              <td>Recharge Subscription ID</td>
              <td>{sub.recharge_subscription_id}</td>
              <td></td>
            </tr>
          )}
          <tr>
            <td>User Profile ID</td>
            <td>{sub.user_profile}</td>
            <td></td>
          </tr>
          <EditableRow
            name="first_name"
            label={prettyKey('first_name')}
            value={sub.first_name}
            type="input"
            save={handleUpdateField}
            thirdRowWidth="255px"
          />
          <EditableRow
            name="last_name"
            label={prettyKey('last_name')}
            value={sub.last_name}
            type="input"
            save={handleUpdateField}
            thirdRowWidth="255px"
          />
          <tr>
            <td>Account Name</td>
            <td>{sub.account_name}</td>
            <td> </td>
          </tr>
          <tr>
            <td>Device SN</td>
            <td>{sub.device_sn}</td>
            <td>
              {sub.device && (
                <Link to={`/simo/devices/${sub.device}/view`}>View Device</Link>
              )}
            </td>
          </tr>
          <tr>
            <td>IMEI</td>
            <td>{sub.imei}</td>
            <td></td>
          </tr>
          <tr>
            <td>Simo Status</td>
            <td>{sub.simo_status}</td>
            <td></td>
          </tr>
          <tr>
            <td>Connection Status</td>
            <td>
              {diagnosticDataLoadingStatus
                ? diagnosticDataLoadingStatus
                : diagnosticData && diagnosticData.connection_status_name}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Coverage</td>
            <td>{sub.coverage}</td>
            <td></td>
          </tr>
          <tr>
            <td>Effective Date</td>
            <td>
              {sub.effective_date
                ? moment(sub.effective_date).format(DATE_TIME_FORMAT)
                : sub.effective_date}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Expiry Date</td>
            <td>
              {sub.expiry_date
                ? moment(sub.expiry_date).format(DATE_TIME_FORMAT)
                : sub.expiry_date}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Expiry Type</td>
            <td>{sub.expiry_type}</td>
            <td></td>
          </tr>
          <tr>
            <td>Activation Type</td>
            <td>{sub.activation_type}</td>
            <td></td>
          </tr>
          <tr>
            <td>Suspension Type</td>
            <td>{sub.suspension_type}</td>
            <td></td>
          </tr>
          <tr>
            <td>Plan</td>
            <td>
              <span>{simoRenderPlan(plan, showPrices)}</span>
            </td>
            <td>
              <Link to={`/simo/subscriptions/${subId}/update-plan`}>
                Update
              </Link>
            </td>
          </tr>
          <tr>
            <td>Upcoming Plan</td>
            <td>
              <span>{simoRenderPlan(upcomingPlanToDisplay, showPrices)}</span>
            </td>
            <td>
              <Link to={`/simo/subscriptions/${subId}/upcoming-plan`}>
                Update
              </Link>
            </td>
          </tr>
          <tr>
            <td>Usage Balance</td>
            <td>
              {balance && (
                <span>
                  {Math.round((toGb(balance) + Number.EPSILON) * 100) / 100}GB
                </span>
              )}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Renewal Enabled</td>
            <td>{sub.renewal_enabled ? 'Yes' : 'No'}</td>
            <td></td>
          </tr>
          <tr>
            <td>Renew At</td>
            <td>
              {sub.renew_at
                ? moment(sub.renew_at).format(DATE_TIME_FORMAT)
                : sub.renew_at}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Agent ID</td>
            <td>{sub.agent_id}</td>
            <td></td>
          </tr>
          <tr>
            <td>Agent Name</td>
            <td>{sub.agent_name}</td>
            <td></td>
          </tr>
          <tr>
            <td className={cn({ 'text-danger': sub.deleted })}>Deleted</td>
            <td className={cn({ 'text-danger': sub.deleted })}>
              {String(sub.deleted)}
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Created By</td>
            <td>
              <UserInfo userId={sub.created_by} type="nameIdRole" />
            </td>
            <td></td>
          </tr>
          <tr>
            <td>Cycle Type</td>
            <td>{sub.cycle_type}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
